import React from 'react'
import { useDispatch, addNotification } from '@ggs/store'
import { useI18n } from '@ggs/i18n'
import { get, isArray, isString, isNumber } from 'lodash'
import { useApiRequest } from './'

/**
 * @typedef {import('../../definitions').CampusesListParams} CampusListParams
 * @typedef {import('../../definitions').CampusRecord} CampusRecord
 * @typedef {import('../../definitions').CampusListResponse} CampusListResponse
 * @typedef {import('../../definitions').BuildingRecord} BuildingRecord
 */

/**
 * @typedef {(params: CampusListParams) => Promise<CampusListResponse>} GetCampuses
 * @typedef {(buildingId: string) => Promise<BuildingRecord>} GetBuilding
 */

/**
 * @typedef {Object} LocationApiHook
 * @property {GetCampuses} getCampuses
 * @property {GetBuilding} getBuilding
 */

/**
 * Hook that expose functions to consume Location API data.
 * @return {LocationApiHook}
 */
function useLocationApi() {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const baseUrl = `${process.env.REACT_APP_LOCATION_BASE_URL}` || 'http://localhost'
  const { doApiRequest, prepareQueryParams } = useApiRequest({
    baseUrl,
  })

  /**
   * Process a API request.
   * @param {string} endpoint An API endpoint path
   * @param {any} data Payload data
   * @param {Object<string, string>=} customHeaders
   * @param {string} method
   * @return {Promise<any>}
   */
  const doRequest = async (endpoint, data = null, customHeaders, method = '') => {
    const proxyEndpoint = `?_api_proxy_uri=${encodeURIComponent(endpoint)}`
    const response = doApiRequest({
      endpoint: proxyEndpoint,
      data,
      customHeaders,
      method,
    })

    const errorCode = get(response, 'errorCode', '')
    if (errorCode) {
      dispatch(
        addNotification(
          'location_api_request_failed',
          'error',
          t('Location API request failed.')
        )
      )

      return null
    }

    return response
  }

  /**
   * Get UBC campuses list.
   * @type {GetCampuses}
   * @param {CampusListParams} params
   * @return {Promise<CampusListResponse>}
   */
  const getCampuses = async (params) => {
    const endpoint = 'campuses'
    const queryParams = prepareQueryParams(params)
    const response = await doRequest(endpoint + '?' + queryParams)

    if (!response?.pageItems) {
      return null
    }

    return response
  }

  /**
   * Get UBC building record.
   * @type {GetBuilding}
   * @param {string} buildingId
   * @return {Promise<BuildingRecord>}
   */
  const getBuilding = async (buildingId) => {
    const endpoint = `buildings/id::${buildingId}`
    const entity = await doRequest(endpoint)

    if (!entity) {
      return null
    }

    return entity
  }

  return {
    getCampuses,
    getBuilding,
  }
}

export default useLocationApi
